import React, { useState, ComponentType, useCallback, useEffect } from "react";
import type { TableColumnsType, TableProps } from "antd";
import { Form, type FormInstance, Modal, notification } from "antd";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";


export interface DataType {
    key: React.Key;
    item_code: string;
    item_name: string;
    uom: string;
    location_rack: string;
    available_qty: number;
    demand_qty: number;
    supply_qty: number;
    pick_qty: number;
    packing_qty: number;
    return_qty?: number;
}

export interface PickingListHeader {
    id: number;
    no_picking: string;
    sales_order: string;
    so_no?: string;
    cs_name?: string;
    transaction_type: string;
    customer_name: string;
    purchase_order: string;
    order_type: string;
    warehouse_name: string;
    warehouse_code: string;
    status: string;
}

export interface WithPickingListReturnLogicProps {
    transformStyle: React.CSSProperties;
    columns: TableColumnsType<DataType>;
    rowSelection: TableProps<DataType>["rowSelection"];
    initialValues: { [key: string]: any };
    form: FormInstance<any>;
    isModalOpen: boolean;
    showModal: () => void;
    handleCancel: () => void;
    tableData: DataType[];
    isItemSelectModalOpen: boolean;
    handlePickingListSelect: (record: PickingListHeader) => void;
    handleItemSelectConfirm: (selectedItems: any[]) => void;
    handleItemSelectCancel: () => void;
    selectedPickingListNo: string | null;
    isAddItemModalOpen: boolean;
    showAddItemModal: () => void;
    handleAddItemCancel: () => void;
    editingItem: DataType | null;
    handleEditItem: (record: DataType) => void;
    handleDeleteItems: () => void;
    selectedRowKeys: React.Key[];
    isItemSelectForAddModalOpen: boolean;
    showItemSelectForAddModal: () => void;
    handleItemSelectForAddCancel: () => void;
    handleItemSelectForAddConfirm: (selectedItems: DataType[]) => void
    handleUpdateItem: (values: { return_qty: number }) => void;
    isEditing: boolean;
    handleAddItem: (values: { return_qty: number }) => void;
    autosaveStatus: 'idle' | 'processing' | 'error' | 'stopped';
    handleSubmit: () => void;
    isRemarkModalOpen: boolean;
    handleConfirmSubmit: (remark: string) => void;
    handleCancelRemarkModal: () => void;
    handleVoid: () => void;
    isProgress: boolean;
    selectedWarehouseCode: string | null;
}

const withPickingListReturnLogic = <P extends object>(
    WrappedComponent: ComponentType<P & WithPickingListReturnLogicProps>
) => {
    const WithLogic: React.FC<P> = (props) => {

        const [form] = Form.useForm();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isItemSelectModalOpen, setIsItemSelectModalOpen] = useState(false);
        const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
        const [isItemSelectForAddModalOpen, setIsItemSelectForAddModalOpen] = useState(false);
        const [selectedPickingListNo, setSelectedPickingListNo] = useState<string | null>(null);
        const [tableData, setTableData] = useState<DataType[]>([]);
        const [selectedHeader, setSelectedHeader] = useState<PickingListHeader | null>(null);
        const [editingItem, setEditingItem] = useState<DataType | null>(null);
        const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
        const [isEditing, setIsEditing] = useState(false);
        const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'processing' | 'error' | 'stopped'>('stopped');
        const [selectedWarehouseCode, setSelectedWarehouseCode] = useState<string | null>(null);
        const [isProgress, setIsProgress] = useState(false);
        const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
        const watchedFormValues = Form.useWatch([], form);
        const location = useLocation();
        const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(true);
        const navigate = useNavigate();

        const showModal = () => {
            setIsModalOpen(true);
        };

        const handleCancel = () => {
            setIsModalOpen(false);
        };

        const showAddItemModal = () => {
            setEditingItem(null);
            setIsEditing(false);
            setIsAddItemModalOpen(true);
        };

        const handleAddItemCancel = () => {
            setIsAddItemModalOpen(false);
            setEditingItem(null);
            setIsEditing(false);
        };

        const handleEditItem = (record: DataType) => {
            setEditingItem(record);
            setIsEditing(true);
            setIsAddItemModalOpen(true);
        };

        const handleDeleteItems = () => {
            const newData = tableData.filter(item => !selectedRowKeys.includes(item.key));
            setTableData(newData);
            setSelectedRowKeys([]);
            notification.success({
                message: 'Items Deleted',
                description: `${selectedRowKeys.length} item(s) have been successfully removed.`,
            });
        };

        const showItemSelectForAddModal = () => {
            if (!form.getFieldValue('picking_list_no')) {
                notification.warning({
                    message: "Picking List Not Selected",
                    description: "Please select a Picking List No before browsing items.",
                });
                return;
            }
            setIsItemSelectForAddModalOpen(true);
        };

        const handleItemSelectForAddCancel = () => {
            setIsItemSelectForAddModalOpen(false);
        };

        const handleItemSelectForAddConfirm = (selectedItems: DataType[]) => {
            if (selectedItems && selectedItems.length > 0) {
                const selectedItem = selectedItems[0];
                setEditingItem(selectedItem);
            }
            setIsItemSelectForAddModalOpen(false);
        };

        const handleAddItem = (values: { return_qty: number }) => {
            if (!editingItem) return;


            const isItemExist = tableData.some(item => item.item_code === editingItem.item_code);
            if (isItemExist) {
                notification.warning({
                    message: 'Item Sudah Ada',
                    description: `Item dengan kode ${editingItem.item_code} sudah ada di dalam daftar.`,
                });
                return;
            }


            const newItem: DataType = {
                ...editingItem,
                return_qty: Number(values.return_qty) || 0,
                key: editingItem.key,
            };

            setTableData(prevData => [...prevData, newItem]);
            handleAddItemCancel();
            notification.success({
                message: 'Item Ditambahkan',
                description: `Item ${newItem.item_code} berhasil ditambahkan.`,
            });
        };

        const handleUpdateItem = (values: { return_qty: number }) => {
            if (!editingItem) return;

            const updatedTableData = tableData.map(item => {
                if (item.key === editingItem.key) {
                    return { ...item, return_qty: Number(values.return_qty) || 0 };
                }
                return item;
            });

            setTableData(updatedTableData);
            handleAddItemCancel();
            notification.success({
                message: 'Item Updated',
                description: `Item ${editingItem.item_code} has been successfully updated.`,
            });
        };

        const handleSubmit = () => {
            const pickingListNo = form.getFieldValue('picking_list_no');
            if (!pickingListNo || tableData.length === 0) {
                notification.warning({
                    message: 'Data Tidak Lengkap',
                    description: 'Pastikan Picking List No dan item detail telah terisi sebelum submit.',
                });
                return;
            }
            const hasInvalidQty = tableData.some(item => (Number(item.return_qty) || 0) < 1);
            if (hasInvalidQty) {
                notification.error({
                    message: 'Validasi Gagal',
                    description: 'Semua item harus memiliki Return Quantity minimal 1.',
                });
                return;
            }
            setIsRemarkModalOpen(true);
        };

        const handleCancelRemarkModal = () => {
            setIsRemarkModalOpen(false);
        };

        const handleConfirmSubmit = async (remark: string) => {
            const pickingListNo = form.getFieldValue('picking_list_no');
            const pickingListReturnNo = form.getFieldValue('picking_list_return_no');
            setIsProgress(true);

            try {
                const payload = {
                    picking_list_no: pickingListNo,
                    picking_list_return_no: pickingListReturnNo,
                    details: tableData,
                    remark: remark,
                };

                const response = await axios.post(`${backendUrl}/api/picking-list-return/submit`, payload);
                await axios.post(`${backendUrl}/api/log-spareparts/picking_list_return`, { picking_list_return_no: pickingListReturnNo });

                notification.success({
                    message: 'Submit Berhasil',
                    description: response.data.message,
                });

                form.resetFields();
                setTableData([]);
                setAutosaveStatus('stopped');
                setIsRemarkModalOpen(false);

            } catch (error: any) {
                console.error("Submit failed:", error);
                notification.error({
                    message: 'Submit Gagal',
                    description: error.response?.data?.message || 'Terjadi kesalahan saat mengirim data ke server.',
                });
            } finally {
                setIsProgress(false);
            }
        };

        const handleVoid = () => {
            const pickingListReturnNo = form.getFieldValue('picking_list_return_no');
            const status = form.getFieldValue('status');

            if (!pickingListReturnNo || status !== 'Draft') {
                notification.warning({
                    message: 'Aksi Tidak Diizinkan',
                    description: 'Void hanya dapat dilakukan pada dokumen dengan status "Draft".',
                });
                return;
            }

            Modal.confirm({
                title: 'Apakah Anda yakin ingin membatalkan (Void) dokumen ini?',
                content: `Dokumen dengan nomor ${pickingListReturnNo} akan dihapus secara permanen.`,
                okText: 'Ya, Void',
                okType: 'danger',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsProgress(true);
                    try {
                        const response = await axios.delete(`${backendUrl}/api/picking-list-return/${pickingListReturnNo}`);
                        notification.success({
                            message: 'Void Berhasil',
                            description: response.data.message,
                        });
                        form.resetFields();
                        setTableData([]);
                        setAutosaveStatus('stopped');
                        navigate('/dashboard/data_picking_return');
                    } catch (error: any) {
                        console.error("Void failed:", error);
                        notification.error({
                            message: 'Void Gagal',
                            description: error.response?.data?.message || 'Terjadi kesalahan saat membatalkan dokumen.',
                        });
                    } finally {
                        setIsProgress(false); // ✅: nonaktifkan progress setelah proses void berakhir
                    }
                },
            });
        };

        const autosave = useCallback(async () => {
            const pickingListNo = form.getFieldValue('picking_list_no');
            if (!pickingListNo) {
                setAutosaveStatus('stopped');
                return;
            }

            setAutosaveStatus('processing');
            try {
                const headerData = form.getFieldsValue(true);
                const processedHeader = { ...headerData };
                processedHeader.warehouse_group = selectedWarehouseCode;
                delete processedHeader.warehouse;

                if (processedHeader.status === 'Draft') {
                    processedHeader.status = 1;
                }

                const payload = {
                    header: processedHeader,
                    details: tableData,
                };

                await axios.post(`${backendUrl}/api/picking-list-return`, payload);
                setAutosaveStatus('idle');
            } catch (error) {
                console.error("Autosave failed:", error);
                setAutosaveStatus('error');
                notification.error({
                    message: 'Autosave Gagal',
                    description: 'Gagal menyimpan perubahan secara otomatis. Silakan periksa konsol.',
                });
            }
        }, [form, tableData, backendUrl, selectedWarehouseCode]);

        useEffect(() => {
            const loadDataFromState = async () => {
                if (location.state?.pickingListReturnNo) {
                    const { pickingListReturnNo, status } = location.state;

                    setIsAutosaveEnabled(false);

                    try {
                        const response = await axios.get(`${backendUrl}/api/picking-list-return/${pickingListReturnNo}`);
                        const { header, details } = response.data;

                        form.setFieldsValue(header);
                        setTableData(details);
                        setSelectedWarehouseCode(header.warehouse_group);

                        if (status === '1' || status === 'Draft') {
                            setIsAutosaveEnabled(true);
                        }

                    } catch (error) {
                        console.error("Failed to fetch picking return data:", error);
                        notification.error({
                            message: 'Fetch Data Failed',
                            description: 'Could not fetch the selected picking return data.'
                        });
                    } finally {
                        window.history.replaceState({}, document.title);
                    }
                }
            };

            loadDataFromState();
        }, [location.state, form, backendUrl]);

        useEffect(() => {
            const pickingListNo = form.getFieldValue('picking_list_no');
            if (pickingListNo && isAutosaveEnabled) {
                const handler = setTimeout(() => {
                    autosave();
                }, 1500);

                return () => {
                    clearTimeout(handler);
                };
            } else {
                setAutosaveStatus('stopped');
            }
        }, [watchedFormValues, tableData, form, autosave, isAutosaveEnabled]);

        const transformStyle = {
            transform: "scale(0.80)",
            transformOrigin: "top left",
            width: "125%",
        };

        const columns: TableColumnsType<DataType> = [
            { title: "Item Code", dataIndex: "item_code", key: "item_code" },
            { title: "Item Name", dataIndex: "item_name", key: "item_name" },
            { title: "UOM", dataIndex: "uom", key: "uom" },
            { title: "Location", dataIndex: "location_rack", key: "location_rack" },
            { title: "Demand Quantity", dataIndex: "demand_qty", key: "demand_qty" },
            { title: "Supply Quantity", dataIndex: "supply_qty", key: "supply_qty" },
            {
                title: <span style={{ color: '#ff4d4f' }}>Return Quantity</span>,
                dataIndex: "return_qty",
                key: "return_qty",
                render: (text) => <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{text}</span>
            },
        ];

        const rowSelection: TableProps<DataType>["rowSelection"] = {
            selectedRowKeys,
            onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
        };

        const initialValues = {
            status: "Draft",
        };

        const getDisplayFormattedDate = (date: Date): string => {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        };

        const getFormattedDate = (date: Date): string => {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = String(date.getFullYear()).substring(2);
            return `${month}${year}`;
        };

        const fetchLatestPickingListReturnNo = async (datePart: string): Promise<string | null> => {
            try {
                const response = await axios.get(`${backendUrl}/api/picking-list-return/latest-no?datePart=${datePart}`);
                return response.data.latestNo;
            } catch (error) {
                console.error("Gagal mengambil nomor Picking List Return terbaru:", error);
                notification.error({ message: "Gagal mengambil nomor Picking List Return terbaru dari server." });
                return null;
            }
        };

        const calculateNextPickingListReturnNo = (latestNo: string | null, datePart: string): string => {
            const prefix = `SPPN-AMP${datePart}`;
            let nextSequence = 1;
            if (latestNo && latestNo.startsWith(prefix)) {
                const existingSequenceStr = latestNo.substring(prefix.length);
                const existingSequence = parseInt(existingSequenceStr, 10);
                if (!isNaN(existingSequence)) {
                    nextSequence = existingSequence + 1;
                }
            }
            const formattedSequence = String(nextSequence).padStart(5, '0');
            return `${prefix}${formattedSequence}`;
        };

        useEffect(() => {
            const today = new Date();
            const displayDate = getDisplayFormattedDate(today);
            form.setFieldsValue({ picking_list_return_date: displayDate });
        }, [form]);

        const handlePickingListSelect = (record: PickingListHeader) => {
            setSelectedHeader(record);
            setSelectedPickingListNo(record.so_no || null);
            setIsModalOpen(false);
            setIsItemSelectModalOpen(true);
        };

        const handleItemSelectConfirm = async (selectedItems: any[]) => {
            if (selectedHeader) {
                setIsProgress(true);
                try {
                    let newReturnNo = form.getFieldValue('picking_list_return_no');
                    if (!newReturnNo) {
                        const today = new Date();
                        const formattedDate = getFormattedDate(today);
                        const latestNo = await fetchLatestPickingListReturnNo(formattedDate);
                        newReturnNo = calculateNextPickingListReturnNo(latestNo, formattedDate);
                    }

                    form.setFieldsValue({
                        picking_list_return_no: newReturnNo,
                        picking_list_no: selectedHeader.so_no,
                        sales_order_no: selectedHeader.so_no,
                        transaction_type: selectedHeader.transaction_type,
                        customer_name: selectedHeader.cs_name,
                        purchase_order_no: selectedHeader.purchase_order,
                        order_type: selectedHeader.order_type,
                        warehouse: selectedHeader.warehouse_name,
                    });
                    setSelectedWarehouseCode(selectedHeader.warehouse_code);
                } catch (err) {
                    console.error("Import failed:", err);
                } finally {
                    setIsProgress(false); 
                }
            }
            const newTableData = selectedItems.map((item, idx) => ({ ...item, key: `${item.id}_${item.location_rack || ''}_${idx}`, return_qty: 0 }));
            setTableData(newTableData);
            setIsItemSelectModalOpen(false);
            setSelectedHeader(null);
            setSelectedPickingListNo(null);
        };

        const handleItemSelectCancel = () => {
            setIsItemSelectModalOpen(false);
            setSelectedHeader(null);
            setSelectedPickingListNo(null);
        };


        const logicProps: WithPickingListReturnLogicProps = {
            transformStyle,
            columns,
            rowSelection,
            initialValues,
            form,
            isModalOpen,
            showModal,
            handleCancel,
            tableData,
            isItemSelectModalOpen,
            handlePickingListSelect,
            handleItemSelectConfirm,
            handleItemSelectCancel,
            selectedPickingListNo,
            isAddItemModalOpen,
            showAddItemModal,
            handleAddItemCancel,
            editingItem,
            handleEditItem,
            handleDeleteItems,
            selectedRowKeys,
            isItemSelectForAddModalOpen,
            showItemSelectForAddModal,
            handleItemSelectForAddCancel,
            handleItemSelectForAddConfirm,
            handleUpdateItem,
            isEditing,
            handleAddItem,
            autosaveStatus,
            handleSubmit,
            isRemarkModalOpen,
            handleConfirmSubmit,
            handleCancelRemarkModal,
            handleVoid,
            isProgress,
            selectedWarehouseCode
        };
        return <WrappedComponent {...(props as P)} {...logicProps} />;
    };
    return WithLogic;
};

export default withPickingListReturnLogic;